import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  Divider,
  Tabs,
  Tab,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {useForm, Controller} from 'react-hook-form';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import {useSelector} from 'react-redux';
import {ENDPOINT} from 'constants';
import {EMAIL_TEMPLATE_KEYS} from 'utils/emailTemplateKey';
import {
  EMAIL_TEMPLATE_VARIABLES,
  EMAIL_TEMPLATE_HELPERS,
  EMAIL_FOOTER_VARIABLES,
  SCHEMA_REFERENCE,
} from 'utils/emailTemplateVariables';
import {buildSampleVariables} from 'utils/sampleVariableValues';

const TEMPLATE_KEY_OPTIONS = Object.entries(EMAIL_TEMPLATE_KEYS).map(
  ([key, value]) => ({
    value,
    label: key
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' '),
  }),
);

const REMINDER_TYPE_OPTIONS = [
  'renewal',
  'due',
  'overdue',
  'cart',
  'order',
  'subscription',
  'item',
  'design_confirm',
  'suspension',
  'payment',
  'support',
  'payment_failed',
  'payment_cancelled',
  'payment_recovered',
  'payment_overdue',
  'refund',
  'trial',
  'invoice',
  'design',
  'account',
  'engagement',
  'marketing',
  'system',
  'legal',
  'admin_alert',
];

const EmailTemplateModal = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  isEdit,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: {errors},
  } = useForm({defaultValues: initialValues});

  const [contentTab, setContentTab] = useState(0); // 0 = Email content, 1 = SMS content

  // Destructure ref out separately — spreading it onto TextField directly would attach
  // RHF's ref to MUI's outer wrapper div instead of the actual textarea, since TextField
  // only forwards `inputRef` (not `ref`) down to the real <textarea> element.
  const {ref: smsBodyRhfRef, ...smsBodyField} = register('smsBody');
  const smsTextareaRef = useRef(null);

  // Inserts {variableName} at the current cursor position in the SMS textarea
  // (falls back to appending at the end if we don't have a live cursor position yet).
  const insertVarIntoSms = variable => {
    const token = `{${variable}}`;
    const el = smsTextareaRef.current;
    const currentValue = watch('smsBody') || '';

    if (!el) {
      setValue('smsBody', currentValue + token, {shouldDirty: true});
      return;
    }

    const start = el.selectionStart ?? currentValue.length;
    const end = el.selectionEnd ?? currentValue.length;
    const newValue =
      currentValue.slice(0, start) + token + currentValue.slice(end);
    setValue('smsBody', newValue, {shouldDirty: true});

    requestAnimationFrame(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Parse availableVariables string into chips for the reference panel
  const availableVars = watch('availableVariables') || '';
  const varChips = availableVars
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);

  const smsBodyValue = watch('smsBody') || '';
  const smsCharCount = smsBodyValue.length;
  const smsOverLimit = smsCharCount > 160;

  // Full variable picker: every field from every relevant database schema, plus the
  // helper functions and footer variables. Deduped by key (a field appearing in more
  // than one source keeps only its first/most-specific listing) so nothing shows twice.
  // "★ Common for <event>" surfaces the subset already confirmed wired into this
  // template's event today; every schema field below it is fair game per your request
  // for full completeness, each tagged wired true/false so it's clear which ones will
  // actually render data today versus which need a developer to wire them in first.
  const watchedReminderType = watch('reminderType');
  const variableOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    const addAll = (list, group) => {
      list.forEach(v => {
        if (seen.has(v.key)) return;
        seen.add(v.key);
        options.push({...v, group, wired: v.wired ?? true});
      });
    };

    const categoryLabel = EMAIL_TEMPLATE_VARIABLES[watchedReminderType]?.label;
    if (categoryLabel) {
      addAll(
        EMAIL_TEMPLATE_VARIABLES[watchedReminderType].variables,
        `★ Common for ${categoryLabel}`,
      );
    }

    Object.entries(SCHEMA_REFERENCE).forEach(([modelName, {fields}]) => {
      addAll(fields, modelName);
    });

    addAll(EMAIL_TEMPLATE_HELPERS, 'Helper functions');
    addAll(EMAIL_FOOTER_VARIABLES, 'Footer (auto-injected)');

    return options;
  }, [watchedReminderType]);

  const {user} = useSelector(state => state.auth);

  // Realistic sample data for BOTH previews below, generated from whichever variables
  // are currently selected — so the admin sees actual substituted text, not literal
  // {braces}, before this template is ever sent for real.
  const sampleVariables = useMemo(
    () => buildSampleVariables(availableVars),
    [availableVars],
  );

  const substituteVariables = text => {
    let result = text || '';
    Object.entries(sampleVariables).forEach(([key, value]) => {
      result = result.split(`{${key}}`).join(value);
    });
    return result;
  };

  // Live email preview — rendered server-side through the exact same pipeline used for
  // real sends (renderEmailTemplate → emailBase.mjml), so this is a true WYSIWYG of what
  // the recipient will receive, not an approximation. Debounced so we're not hitting the
  // API on every keystroke.
  const emailSubjectValue = watch('subject') || '';
  const emailBodyValue = watch('body') || '';
  const bodyFormatValue = watch('bodyFormat') || 'text';
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);

  useEffect(() => {
    if (!emailBodyValue) {
      setEmailPreviewHtml('');
      return undefined;
    }

    setEmailPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const {data} = await axios.post(
          `${ENDPOINT}api/v1/admin/reminder-templates/preview`,
          {
            subject: emailSubjectValue,
            body: emailBodyValue,
            bodyFormat: bodyFormatValue,
            variables: sampleVariables,
          },
          {headers: {Authorization: `Bearer ${user?.accessToken}`}},
        );
        setEmailPreviewHtml(data.data.html);
      } catch (err) {
        console.error('Failed to render email preview:', err);
      } finally {
        setEmailPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailSubjectValue,
    emailBodyValue,
    bodyFormatValue,
    JSON.stringify(sampleVariables),
    user?.accessToken,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Email Template' : 'Create Email Template'}
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Variable reference panel (only when editing / variables exist) ── */}
        {isEdit && varChips.length > 0 && (
          <MDBox mb={2} p={1.5} sx={{bgcolor: 'grey.100', borderRadius: 1}}>
            <MDTypography
              variant="caption"
              fontWeight="medium"
              display="block"
              mb={0.5}>
              Available variables for this template — use these in Subject and
              Body as{' '}
              <code
                style={{
                  background: '#e0e0e0',
                  padding: '0 4px',
                  borderRadius: 3,
                }}>
                {'{variableName}'}
              </code>
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {varChips.map(v => (
                <Chip
                  key={v}
                  label={`{${v}}`}
                  size="small"
                  variant="outlined"
                  sx={{fontFamily: 'monospace', fontSize: '0.72rem'}}
                  onClick={() => navigator.clipboard?.writeText(`{${v}}`)}
                  title="Click to copy"
                />
              ))}
            </MDBox>
          </MDBox>
        )}

        <form id="email-template-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} mt={0.5}>
            {/* Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                {...register('name', {required: 'Name is required'})}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </Grid>

            {/* Template Key — dropdown, locked when editing */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="templateKey"
                control={control}
                rules={{required: 'Template key is required'}}
                render={({field}) => (
                  <TextField
                    {...field}
                    select
                    label="Template Key"
                    fullWidth
                    disabled={isEdit}
                    error={!!errors.templateKey}
                    helperText={
                      isEdit
                        ? 'Locked — changing this breaks the system lookup'
                        : errors.templateKey?.message ||
                          'Select the event this template is for'
                    }
                    sx={
                      isEdit
                        ? {'& .MuiInputBase-root': {bgcolor: 'grey.100'}}
                        : {}
                    }>
                    {TEMPLATE_KEY_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Reminder Type — dropdown */}
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="reminderType"
                control={control}
                rules={{required: 'Reminder type is required'}}
                render={({field}) => (
                  <TextField
                    {...field}
                    select
                    label="Reminder Type"
                    fullWidth
                    error={!!errors.reminderType}
                    helperText={
                      errors.reminderType?.message ||
                      'Used to group templates by category'
                    }>
                    {REMINDER_TYPE_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Available Variables — picked from the schema for the selected Reminder Type, not free-typed */}
            <Grid item xs={12}>
              <Controller
                name="availableVariables"
                control={control}
                render={({field}) => {
                  const selectedKeys = (field.value || '')
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean);
                  const selectedOptions = selectedKeys.map(
                    key =>
                      variableOptions.find(opt => opt.key === key) || {
                        key,
                        group: 'Other',
                      },
                  );

                  return (
                    <Autocomplete
                      multiple
                      options={variableOptions}
                      groupBy={opt => opt.group}
                      getOptionLabel={opt => opt.key}
                      isOptionEqualToValue={(opt, val) => opt.key === val.key}
                      value={selectedOptions}
                      onChange={(_, newValue) => {
                        field.onChange(newValue.map(opt => opt.key).join(','));
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.key}>
                          <MDBox
                            display="flex"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            width="100%"
                            gap={1}>
                            <MDBox>
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                                sx={{fontFamily: 'monospace'}}>
                                {`{${option.key}}`}
                              </MDTypography>
                              {option.description && (
                                <MDTypography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary">
                                  {option.description}
                                </MDTypography>
                              )}
                            </MDBox>
                            {option.wired === false && (
                              <Chip
                                label="not wired yet"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{fontSize: '0.65rem', flexShrink: 0}}
                              />
                            )}
                          </MDBox>
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({index})}
                            key={option.key}
                            label={`{${option.key}}`}
                            size="small"
                            color={
                              option.wired === false ? 'warning' : 'default'
                            }
                            title={
                              option.wired === false
                                ? 'Not yet passed to this template by any backend code — will render blank until wired in'
                                : undefined
                            }
                            sx={{fontFamily: 'monospace'}}
                          />
                        ))
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Available Variables"
                          helperText={
                            (watchedReminderType
                              ? `"★ Common for ${
                                  EMAIL_TEMPLATE_VARIABLES[watchedReminderType]
                                    ?.label || watchedReminderType
                                }" is confirmed working today. `
                              : '') +
                            'Browse every database field by schema below — fields tagged "not wired yet" exist in the database but need a developer to pass them into this email/SMS before they\'ll show real data.'
                          }
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Email / SMS content tabs */}
            <Grid item xs={12}>
              <Tabs
                value={contentTab}
                onChange={(_, value) => setContentTab(value)}
                sx={{
                  mb: 2,
                  minHeight: 36,
                  '& .MuiTab-root': {minHeight: 36, py: 1},
                }}>
                <Tab label="Email content" />
                <Tab label="SMS content (optional)" />
              </Tabs>

              {/* ── Email content ── */}
              {contentTab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      label="Email Subject"
                      fullWidth
                      {...register('subject')}
                      helperText="Use {variableName} for dynamic values, e.g.  Your order #{orderId} is confirmed"
                    />
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mt={2}
                      mb={0.5}>
                      <MDTypography variant="caption" fontWeight="medium">
                        Email Body
                      </MDTypography>
                      <Controller
                        name="bodyFormat"
                        control={control}
                        render={({field}) => (
                          <ToggleButtonGroup
                            value={field.value || 'text'}
                            exclusive
                            size="small"
                            onChange={(_, value) =>
                              value && field.onChange(value)
                            }>
                            <ToggleButton
                              value="text"
                              sx={{py: 0.25, px: 1.5, textTransform: 'none'}}>
                              Plain text
                            </ToggleButton>
                            <ToggleButton
                              value="html"
                              sx={{py: 0.25, px: 1.5, textTransform: 'none'}}>
                              HTML code
                            </ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                    </MDBox>
                    <TextField
                      fullWidth
                      multiline
                      minRows={12}
                      {...register('body')}
                      helperText={
                        bodyFormatValue === 'html'
                          ? 'Use {variableName} placeholders. Write real HTML — e.g. <strong>bold</strong>, <span style="color:#ff0000;">colored text</span> — it\'s inserted as-is, with no auto-formatting.'
                          : 'Use {variableName} placeholders. Formatting: **bold text**, - bullet point'
                      }
                      inputProps={{
                        style: {
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          lineHeight: 1.6,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}>
                      <MDTypography variant="caption" fontWeight="medium">
                        Live preview — exactly how the recipient will see it
                      </MDTypography>
                      {emailPreviewLoading && (
                        <MDTypography variant="caption" color="text.secondary">
                          Rendering…
                        </MDTypography>
                      )}
                    </MDBox>
                    {emailPreviewHtml ? (
                      <iframe
                        title="Email preview"
                        srcDoc={emailPreviewHtml}
                        style={{
                          width: '100%',
                          height: 480,
                          border: '1px solid #e0e0e0',
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      <MDBox
                        sx={{
                          width: '100%',
                          height: 480,
                          border: '1px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'text.secondary',
                          fontSize: 13,
                          textAlign: 'center',
                          px: 2,
                        }}>
                        Start typing the Email Body to see a live preview
                      </MDBox>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* ── SMS content (optional) ── */}
              {contentTab === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    {varChips.length > 0 && (
                      <MDBox mb={1}>
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          display="block"
                          mb={0.5}>
                          Insert variable — click to add at cursor
                        </MDTypography>
                        <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                          {varChips.map(v => (
                            <Chip
                              key={v}
                              label={`{${v}}`}
                              size="small"
                              onClick={() => insertVarIntoSms(v)}
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.72rem',
                                cursor: 'pointer',
                              }}
                            />
                          ))}
                        </MDBox>
                      </MDBox>
                    )}
                    <TextField
                      label="SMS Message"
                      fullWidth
                      multiline
                      minRows={6}
                      {...smsBodyField}
                      inputRef={el => {
                        smsBodyRhfRef(el);
                        smsTextareaRef.current = el;
                      }}
                      helperText="Use {variableName} placeholders. Optional — leave blank to send this event by email only."
                      inputProps={{
                        style: {
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          lineHeight: 1.6,
                        },
                      }}
                    />
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        fontFamily: 'monospace',
                        color: smsOverLimit ? 'error.main' : 'text.secondary',
                      }}>
                      {smsCharCount} / 160 characters
                      {smsOverLimit
                        ? ' — will send as multiple SMS segments'
                        : ''}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      display="block"
                      mb={1}>
                      Live preview — with sample data substituted
                    </MDTypography>
                    <MDBox
                      sx={{
                        maxWidth: 260,
                        minHeight: 70,
                        bgcolor: 'grey.100',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: '16px 16px 16px 4px',
                        p: 2,
                        fontSize: 13,
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                      }}>
                      {smsBodyValue
                        ? substituteVariables(smsBodyValue)
                        : 'SMS preview will appear here…'}
                    </MDBox>
                  </Grid>
                </Grid>
              )}
            </Grid>

            {/* Description (optional, admin-only) */}
            <Grid item xs={12}>
              <TextField
                label="Description (optional, admin reference only)"
                fullWidth
                {...register('description')}
                helperText="Brief note about when this email is sent — not shown to users"
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{px: 3, py: 2}}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          form="email-template-form"
          variant="contained"
          color="primary">
          {isEdit ? 'Update Template' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateModal;
